import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Box, Flex, Text, Badge, HStack, VStack, Select, Input, Button,
  Table, Thead, Tbody, Tr, Th, Td, Spinner, FormControl, FormLabel,
  Tooltip,
} from '@chakra-ui/react';
import { FiAlertCircle, FiRefreshCw, FiExternalLink } from 'react-icons/fi';
import api from '../api';
import IncidentBlogPage from './IncidentBlogPage';

const STATUS_COLORS = {
  open: 'red', discovery: 'orange', action: 'yellow',
  reporting: 'blue', closed: 'green', closed_reported: 'teal',
};
const STATUS_LABELS = {
  open: 'Open', discovery: 'Discovery', action: 'Action',
  reporting: 'Reporting', closed: 'Closed', closed_reported: 'Closed & Reported',
};

const fmtDt = (d) => d ? new Date(d).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit',
}) : '—';

const toDateInput = (d) => d.toISOString().split('T')[0];

const threeMonthsAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return toDateInput(d);
};

export default function IncidentDashboard({ isOpen, onClose }) {
  const [drillIncident, setDrillIncident] = useState(null); // { taskId, taskTitle, projectId, incidentId }
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState({ open: 0, closed: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('open');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      // Fetch ALL incidents (no filter, no date range) for the summary stats
      const { data } = await api.get('/incidents/dashboard');
      const open = (data || []).filter(i => i.is_open).length;
      const closed = (data || []).filter(i => !i.is_open).length;
      setStats({ open, closed, total: (data || []).length });
    } catch(e) {}
  }, []);

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status_filter: filter });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo)   params.set('date_to', dateTo);
      const { data } = await api.get(`/incidents/dashboard?${params}`);
      setIncidents(data || []);
    } catch(e) {}
    setLoading(false);
  }, [filter, dateFrom, dateTo]);

  useEffect(() => {
    if (isOpen) { fetchIncidents(); fetchStats(); }
  }, [isOpen, fetchIncidents, fetchStats]);

  // When filter changes, set default date range
  const handleFilterChange = (val) => {
    setFilter(val);
    if (val === 'open') {
      setDateFrom('');
      setDateTo('');
    } else {
      setDateFrom(threeMonthsAgo());
      setDateTo(toDateInput(new Date()));
    }
  };

  const openCount = stats.open;
  const closedCount = stats.closed;

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader pb={2}>
          <HStack spacing={2}>
            <FiAlertCircle color="#EE7203" />
            <Text>Incident Log — Dashboard</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Filters */}
          <Flex gap={3} mb={4} flexWrap="wrap" align="flex-end">
            <FormControl w="220px">
              <FormLabel fontSize="xs" mb={0.5}>View</FormLabel>
              <Select size="sm" value={filter} onChange={e => handleFilterChange(e.target.value)}>
                <option value="open">All Open Incidents</option>
                <option value="closed">Closed (not reported)</option>
                <option value="closed_reported">Closed &amp; Reported</option>
              </Select>
            </FormControl>
            <FormControl w="160px">
              <FormLabel fontSize="xs" mb={0.5}>From</FormLabel>
              <Input size="sm" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                placeholder={filter === 'open' ? 'All dates' : ''} />
            </FormControl>
            <FormControl w="160px">
              <FormLabel fontSize="xs" mb={0.5}>To</FormLabel>
              <Input size="sm" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </FormControl>
            <Button size="sm" leftIcon={<FiRefreshCw size={12} />} variant="outline" onClick={() => { fetchIncidents(); fetchStats(); }}>
              Refresh
            </Button>
            {/* Summary stats */}
            <HStack spacing={3} ml="auto">
              <Box textAlign="center" px={3} py={1} bg="red.50" borderRadius="md" border="1px" borderColor="red.200">
                <Text fontSize="18px" fontWeight="800" color="red.600">{openCount}</Text>
                <Text fontSize="10px" color="red.500">Open</Text>
              </Box>
              <Box textAlign="center" px={3} py={1} bg="green.50" borderRadius="md" border="1px" borderColor="green.200">
                <Text fontSize="18px" fontWeight="800" color="green.600">{closedCount}</Text>
                <Text fontSize="10px" color="green.500">Closed</Text>
              </Box>
              <Box textAlign="center" px={3} py={1} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                <Text fontSize="18px" fontWeight="800" color="gray.600">{stats.total}</Text>
                <Text fontSize="10px" color="gray.500">Total (all)</Text>
              </Box>
            </HStack>
          </Flex>

          {loading && <Box py={12} textAlign="center"><Spinner color="red.400" /></Box>}

          {!loading && incidents.length === 0 && (
            <Box textAlign="center" py={12}>
              <Box fontSize="40px" mb={3}>🛡️</Box>
              <Text fontSize="14px" color="gray.500">No incidents found for this filter</Text>
            </Box>
          )}

          {!loading && incidents.length > 0 && (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr bg="gray.50">
                    <Th fontSize="9px" w="8px"></Th>
                    <Th fontSize="9px">Status</Th>
                    <Th fontSize="9px">Title</Th>
                    <Th fontSize="9px">Project</Th>
                    <Th fontSize="9px">Task</Th>
                    <Th fontSize="9px">Started</Th>
                    <Th fontSize="9px">Entries</Th>
                    <Th fontSize="9px">Flags</Th>
                    <Th fontSize="9px">Noticed By</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {incidents.map(inc => (
                    <Tr key={inc.id}
                      cursor="pointer"
                      _hover={{ bg: 'blue.50' }}
                      onClick={() => setDrillIncident({
                        taskId: inc.task_id,
                        taskTitle: inc.task_title,
                        projectId: inc.project_id_int,
                        incidentId: inc.uuid || inc.id,
                      })}
                      borderLeft="3px solid"
                      sx={{ borderLeftColor: inc.is_open ? '#FC8181' : '#68D391' }}>
                      <Td px={1}>
                        <Box w="8px" h="8px" borderRadius="full"
                          bg={inc.is_open ? 'red.400' : 'green.400'} />
                      </Td>
                      <Td px={2}>
                        <Badge colorScheme={STATUS_COLORS[inc.status] || 'gray'} fontSize="8px">
                          {STATUS_LABELS[inc.status] || inc.status}
                        </Badge>
                      </Td>
                      <Td px={2} maxW="220px">
                        <Text fontSize="12px" fontWeight="700" noOfLines={2}>{inc.title}</Text>
                      </Td>
                      <Td px={2}>
                        <Text fontSize="11px" color="gray.600" noOfLines={1}>
                          {inc.row_group_name ? `${inc.row_group_name} / ` : ''}{inc.project_name || '—'}
                        </Text>
                      </Td>
                      <Td px={2}>
                        <Text fontSize="11px" color="gray.500" noOfLines={1}>{inc.task_title || '—'}</Text>
                      </Td>
                      <Td px={2}>
                        <Text fontSize="11px" color="gray.500" whiteSpace="nowrap">{fmtDt(inc.started_at)}</Text>
                      </Td>
                      <Td px={2} textAlign="center">
                        <Badge colorScheme="gray" fontSize="8px">{inc.row_count || 0}</Badge>
                      </Td>
                      <Td px={2}>
                        <HStack spacing={0.5} flexWrap="wrap">
                          {inc.data_leaked && <Badge colorScheme="red" fontSize="7px">Leak</Badge>}
                          {inc.data_lost && <Badge colorScheme="red" fontSize="7px">Loss</Badge>}
                          {inc.system_unavailable && <Badge colorScheme="orange" fontSize="7px">SysDown</Badge>}
                          {inc.pr_issue && <Badge colorScheme="pink" fontSize="7px">PR</Badge>}
                          {inc.non_disruptive && <Badge colorScheme="green" fontSize="7px">NonDisrupt</Badge>}
                        </HStack>
                      </Td>
                      <Td px={2}>
                        <Text fontSize="11px" color="gray.500">{inc.noticed_by_name || '—'}</Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>

    {/* Drill-down: open the incident log for the clicked incident's task */}
    {drillIncident && (
      <IncidentBlogPage
        taskId={drillIncident.taskId}
        taskTitle={drillIncident.taskTitle}
        projectId={drillIncident.projectId}
        initialIncidentId={drillIncident.incidentId}
        isOpen={!!drillIncident}
        onClose={() => setDrillIncident(null)}
        onRefresh={() => { setDrillIncident(null); fetchIncidents(); fetchStats(); }}
      />
    )}
    </>
  );
}
